import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ContentChild, effect, ElementRef, inject, input, OnDestroy, output, signal, TemplateRef, ViewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CanvasEditorComponent } from 'src/app/views/content/canvas-editor/canvas-editor.component';
import { CodeEditorCanvasComponent } from 'src/app/views/content/canvas-editor/code-editor-canvas/code-editor-canvas.component';
import { ChunkFileDisplayerComponent } from 'src/app/views/content/chunk/chunk-file-displayer/chunk-file-displayer.component';
import { AIPaneData, ContentStateService } from 'src/app/views/content/content-state.service';
import { GenerateContentPaneComponent } from 'src/app/views/content/generate-content-pane/generate-content-pane.component';
import { CourseSettings } from 'src/app/views/displayer/courses/courses.dto';
import { QuestionsAnalysisViewComponent } from 'src/app/views/questions/questions-analysis-view/questions-analysis-view.component';
import { QuestionsSearchViewComponent } from 'src/app/views/questions/questions-search-view/questions-search-view.component';
import { SidenavService } from '../../../../shared/services/sidenav.service';
import { WhiteboardComponent } from '../../whiteboard/whiteboard.component';
import { ChatBoxComponent } from '../../video-call-drawer-chime/chat-box/chat-box.component';
import { ChimeService } from '../../video-call-drawer-chime/chime.service';
import { VideoCallStateService } from '../../video-call-drawer-chime/video-call-state.service';
export { LoadingState } from 'src/app/views/content/content-state.service';

@Component({
  selector: 'app-content-display-card',
  template: `
    <div class="content-card-wrapper">
      <header class="content-card-header">
        <button mat-icon-button class="toggle-sidebar-btn" (click)="sidenavService.toggle()">
          <mat-icon>{{ sidenavService.isSmallOrTablet() ? 'menu' : 'left_panel_open' }}</mat-icon>
        </button>
        <div class="header-center">
          <ng-container *ngTemplateOutlet="headerRef"></ng-container>
        </div>
        <div class="header-right d-flex align-items-center gap-1">
          @if (chimeService.isMeetingActive()) {
            <button
              mat-icon-button
              btnType="action-icon"
              (click)="toggleWhiteboard()"
              [matTooltip]="'VideoCall.Whiteboard.Toggle' | translate"
              [color]="whiteboardOpened() ? 'primary' : ''"
            >
              <mat-icon>gesture</mat-icon>
            </button>
          }
          <ng-container *ngTemplateOutlet="headerActionsRef"></ng-container>
        </div>
      </header>

      <main #scrollableBody class="content-card-body" [class.pane-open]="paneState() !== 'viewing' || chatPaneState() === 'open' || whiteboardOpened()" cdkScrollable>
        <!-- Main content, always present but hidden when pane is open -->
        <div class="main-content-wrapper" [class.hidden]="paneState() !== 'viewing' || chatPaneState() === 'open' || whiteboardOpened()">
          <ng-container *ngTemplateOutlet="bodyRef"></ng-container>
        </div>
      </main>

      <!-- Pane container, slides in over the main content & header -->
      @if (paneState() === 'paneOpen') {
        <div class="pane-container" [class.is-open]="paneState() === 'paneOpen'">
          <div class="h-full flex flex-col pt-1">
            <app-generate-content-pane
              [contentID]="contentID()"
              [paneData]="paneData()"
              [settings]="settings()"
              (settingsChanged)="onUpdateSettings($event)"
              (closed)="handlePaneClose()"
            />
          </div>
        </div>
      }
      @if (chatPaneState() === 'open') {
        <div class="pane-container" [class.is-open]="chatPaneState() === 'open'">
          @defer (on immediate) {
            <app-chat-box />
          }
        </div>
      }
      @if (whiteboardOpened()) {
        <div class="pane-container" [class.is-open]="whiteboardOpened()">
          @defer (on immediate) {
            <room-whiteboard />
          }
        </div>
      }
      @if (paneState() === 'whiteboardEditorOpen') {
        <div class="pane-container" [class.is-open]="paneState() === 'whiteboardEditorOpen'">
          <room-whiteboard
            [chunkId]="whiteboardEditorData()?.chunkId"
            [contentId]="whiteboardEditorData()?.contentId"
            [edition]="whiteboardEditorData()?.edition"
            [isEditingChunk]="true"
            [readOnly]="contentStateService.isReader()"
            (close)="handlePaneClose()"
          />
        </div>
      }
      @if (paneState() === 'canvasOpen') {
        <div class="pane-container" [class.is-open]="paneState() === 'canvasOpen'">
          <app-canvas-editor
            [contentId]="canvasData()?.contentId"
            [messageId]="canvasData()?.messageId"
            [title]="canvasData()?.title"
            [content]="canvasData()?.html"
            [source]="canvasData()?.source"
            [draftId]="canvasData()?.draftId"
            [chunkId]="canvasData()?.chunkId"
            [edition]="canvasData()?.edition"
            (close)="handlePaneClose()"
          >
          </app-canvas-editor>
        </div>
      }
      @if (paneState() === 'analysisOpen') {
        <div class="pane-container" [class.is-open]="paneState() === 'analysisOpen'">
          <app-questions-analysis-view [analysisResult]="analysisResult()" (close)="handlePaneClose()"> </app-questions-analysis-view>
        </div>
      }
      @if (paneState() === 'searchOpen') {
        <div class="pane-container" [class.is-open]="paneState() === 'searchOpen'">
          <app-questions-search-view (close)="handlePaneClose()"> </app-questions-search-view>
        </div>
      }
      @if (paneState() === 'fileViewerOpen') {
        <div class="pane-container" [class.is-open]="paneState() === 'fileViewerOpen'">
          <app-chunk-file-displayer
            [fileLink]="fileViewerData()!.fileLink"
            [fileName]="fileViewerData()!.fileName"
            [fileType]="fileViewerData()!.fileType"
            (closed)="handlePaneClose()"
          />
        </div>
      }
      @if (paneState() === 'codeEditorOpen') {
        <div class="pane-container" [class.is-open]="paneState() === 'codeEditorOpen'">
          <app-code-editor-canvas
            [contentId]="codeEditorData()!.contentId"
            [chunkId]="codeEditorData()!.chunkId"
            [chunkTitle]="codeEditorData()!.title"
            [content]="codeEditorData()!.content"
            [codeLanguage]="codeEditorData()!.codeLanguage"
            [edition]="codeEditorData()!.edition"
            (close)="handlePaneClose()"
          />
        </div>
      }
    </div>
  `,
  styleUrls: ['./content-display-card.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    ScrollingModule,
    MatProgressSpinnerModule,
    GenerateContentPaneComponent,
    ChatBoxComponent,
    WhiteboardComponent,
    CanvasEditorComponent,
    CodeEditorCanvasComponent,
    QuestionsAnalysisViewComponent,
    QuestionsSearchViewComponent,
    ChunkFileDisplayerComponent,
    MatTooltipModule,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentDisplayCardComponent implements OnDestroy {
  contentID = input.required<string>();
  settings = input<CourseSettings | null>(null);
  updateSettings = output<CourseSettings>();

  onUpdateSettings(settings: CourseSettings) {
    this.updateSettings.emit(settings);
  }

  @ContentChild('header') headerRef?: TemplateRef<any>;
  @ContentChild('headerActions') headerActionsRef?: TemplateRef<any>;
  @ContentChild('body') bodyRef?: TemplateRef<any>;
  @ViewChild('scrollableBody') scrollableBody: ElementRef<HTMLElement>;

  paneState = signal<'viewing' | 'paneOpen' | 'canvasOpen' | 'analysisOpen' | 'searchOpen' | 'fileViewerOpen' | 'codeEditorOpen' | 'whiteboardEditorOpen'>('viewing');
  paneData = signal<AIPaneData | null>(null);
  canvasData = signal<{
    title: string;
    html: string;
    messageId?: string;
    contentId?: string;
    source?: 'conversation' | 'builder' | 'transcript' | 'import';
    draftId?: string;
    chunkId?: string;
    edition?: any;
  } | null>(null);
  codeEditorData = signal<{ contentId: string; chunkId: string; title: string; content: string; codeLanguage: string; edition: any } | null>(null);
  whiteboardEditorData = signal<{ contentId: string; chunkId: string; title: string; edition: any } | null>(null);
  analysisResult = signal<any>(null);
  fileViewerData = signal<{ fileLink: string; fileName: string; fileType: string } | null>(null);

  private destroy$ = new Subject<void>();
  public contentStateService = inject(ContentStateService);
  private videoCallStateService = inject(VideoCallStateService);
  public chimeService = inject(ChimeService);
  public sidenavService = inject(SidenavService);
  chatPaneState = this.videoCallStateService.chatPaneState;
  whiteboardOpened = this.videoCallStateService.whiteboardOpened;
  private lastScrollPosition = 0;

  constructor() {
    this.sidenavService.showGlobalToggle.set(false);

    this.contentStateService.openAIPaneCommand$.pipe(takeUntil(this.destroy$)).subscribe((data: AIPaneData) => {
      this.paneData.set(data);
    });

    this.contentStateService.openCanvasCommand$.pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      this.canvasData.set(data);
      this.openPane('canvasOpen');
    });

    this.contentStateService.openWhiteboardEditorCommand$.pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      this.whiteboardEditorData.set(data);
      this.openPane('whiteboardEditorOpen');
    });

    this.contentStateService.openContentGeneratorDraftCommand$.pipe(takeUntil(this.destroy$)).subscribe((draft: any) => {
      this.canvasData.set({
        title: draft.title,
        html: draft.content,
        draftId: draft._id,
        source: 'builder',
      } as any);
      this.openPane('canvasOpen');
    });

    this.contentStateService.openFileViewerCommand$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.fileViewerData.set(data);
      this.openPane('fileViewerOpen');
    });

    this.contentStateService.openCodeEditorCommand$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.codeEditorData.set(data);
      this.openPane('codeEditorOpen');
    });

    toObservable(this.chatPaneState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state === 'open') {
          this.openPane();
        } else {
          this.closePane();
        }
      });

    toObservable(this.whiteboardOpened)
      .pipe(takeUntil(this.destroy$))
      .subscribe((open) => {
        if (open) {
          if (this.paneState() !== 'viewing') {
            this.closePane();
          }
        }
      });

    effect(() => {
      const res = this.contentStateService.questionsAnalysisResult();
      const searchOpen = this.contentStateService.isSearchOpen();

      if (res) {
        this.analysisResult.set(res);
        this.openPane('analysisOpen');
      } else if (this.paneState() === 'analysisOpen') {
        this.closePane();
      }

      if (searchOpen) {
        this.openPane('searchOpen');
      } else if (this.paneState() === 'searchOpen') {
        this.closePane();
      }
    });

    effect(() => {
      if (this.videoCallStateService.drawerOpened()) {
        this.closePane();
      }
    });
  }

  ngOnDestroy(): void {
    this.sidenavService.showGlobalToggle.set(true);
    this.destroy$.next();
    this.destroy$.complete();
  }

  public openPane(state: 'paneOpen' | 'canvasOpen' | 'analysisOpen' | 'searchOpen' | 'fileViewerOpen' | 'codeEditorOpen' | 'whiteboardEditorOpen' = 'paneOpen'): void {
    this.closeWhiteboard();
    if (this.scrollableBody) {
      this.lastScrollPosition = this.scrollableBody.nativeElement.scrollTop;
    }
    this.paneState.set(state);
    // Use a timeout to ensure the DOM has updated before scrolling
    setTimeout(() => {
      if (this.scrollableBody) {
        this.scrollableBody.nativeElement.scrollTop = 0;
      }
    }, 0);
  }

  public closePane(): void {
    this.paneState.set('viewing');
    // Use a timeout to ensure the main content is visible before restoring scroll
    setTimeout(() => {
      if (this.scrollableBody) {
        this.scrollableBody.nativeElement.scrollTop = this.lastScrollPosition;
      }
    }, 0);
  }

  handlePaneClose(): void {
    if (this.paneState() === 'analysisOpen') {
      this.contentStateService.setQuestionsAnalysisResult(null);
    }
    if (this.paneState() === 'searchOpen') {
      this.contentStateService.setSearchOpen(false);
    }
    if (this.paneState() === 'fileViewerOpen') {
      // Clear file viewer data if needed, but not strictly necessary as it's event driven
    }
    this.closePane();
  }

  toggleWhiteboard() {
    this.chimeService.toggleWhiteboard();
  }

  closeWhiteboard() {
    this.chimeService.closeWhiteboard();
  }
}
